import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { AIAgentSidebarMobile } from '@/components/ai-agents/sidebar';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { DesktopChatLayout } from '@/components/ai-agents/DesktopChatLayout';
import { X } from 'lucide-react';

interface MobileChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileChatModal: React.FC<MobileChatModalProps> = ({ isOpen, onClose }) => {
  // ✅ CLEAN: No external event handling needed - everything uses localStorage directly
  const { isMobile } = useIsMobile();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - covers only content area, not navbars */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-16 bottom-16 left-0 right-0 z-35 bg-black/40 backdrop-blur-md"
            onClick={onClose}
          />
          
          {/* Modal Content - positioned between navbars with layout animations */}
          <motion.div
            layout
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 400, 
              damping: 40,
              duration: 0.4 
            }}
            style={{ 
              zIndex: 50,
              willChange: 'transform',
              isolation: 'isolate'
            }}
            className="fixed top-16 bottom-16 left-0 right-0 z-50 flex flex-col bg-background shadow-lg border-t border-border overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✅ CLEAN: Responsive content - mobile sidebar or desktop layout */}
            {isMobile ? (
              <AIAgentSidebarMobile onClose={onClose} />
            ) : (
              <DesktopChatLayout onClose={onClose} />
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileChatModal; 